/**
 * @lines98/client/lib/asset-preload — предзагрузка + ДЕКОД растровых мета-ассетов с
 * живыми ссылками (`meta-gems-resource.md` T2 §5 fix «вщёлкивание»).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-приоритет — звери и контуры камней «вщёлкиваются»
 * при входе в Мастерскую): при пересборке DOM экрана браузер заново ДЕКОДИРУЕТ
 * PNG-спрайты зверей/камней. Owner-наблюдение: «такое чувство, что они из кэша
 * уходят» — это повторный декод при перезаходе, не сетевой промах. Лечение: один раз
 * на старте приложения (или входе в Мастерскую) загрузить и `img.decode()` все спрайты,
 * затем держать ДЕКОДИРОВАННЫЕ `HTMLImageElement` в живой Map<url, img> в памяти модуля.
 * Пока ссылка жива — браузер не выбрасывает декодированный битмап → пересборка DOM
 * (`<img src=…>` / CSS-маска того же url) рисует мгновенно, без повторного декода.
 *
 * **Переиспользуемый модуль (CLAUDE.md «ствол → ветки»):** не копия по экранам. Список
 * URL — единый источник `components/meta-icons.META_ASSET_URLS` (добавил спрайт → попал
 * и в глифы, и сюда). Идемпотентно: повторный `preloadMetaAssets()` не дёргает сеть/декод
 * заново (Map уже держит ссылки) — экран зовёт его при каждом монтировании безопасно.
 *
 * **Контуры камней (`gemEmptyGlyph`):** пустое гнездо — CSS-МАСКА по gem-PNG
 * (`mask: url(gem.png)`). ВАЖНО (owner-bug 2026-06-11, голый аккаунт): декод МАСКИ —
 * ОТДЕЛЬНЫЙ ресурс от `<img>`-декода. Img-Map выше держит только img-битмапы → силуэты
 * «вщёлкивались» даже при прогретых камнях (маска декодилась заново при первом пейнте).
 * Поэтому маски прогреваются ОТДЕЛЬНО — `warmGemMasks()` монтирует скрытый-но-ОТРИСОВАННЫЙ
 * набор реальных `gemEmptyGlyph` (7 цветов) и держит контейнер живым → браузер декодирует
 * маски и удерживает их (симметрично img-Map) → силуэты рисуются мгновенно.
 *
 * SCOPE: предзагрузка+декод+живые ссылки растровых мета-ассетов. NOT IN SCOPE: SVG-глифы
 * (карат/искра — inline, декода не требуют), board-скин (свой канон-pipeline).
 *
 * Cross-references:
 *   - components/meta-icons.ts (`META_ASSET_URLS` — единый список; `gemGlyph` /
 *     `jewelryGlyph` / `gemEmptyGlyph` — потребители тех же URL).
 *   - meta-gems-resource.md §5 (fix предзагрузки/декода); ui-performance SKILL
 *     (декод вне main-thread через img.decode()).
 */

import type { ColorIndex } from '@lines98/shared/types/game';

import { META_ASSET_URLS, gemEmptyGlyph } from '../components/meta-icons.js';

/**
 * Живые ссылки на ДЕКОДИРОВАННЫЕ изображения (url → img). Держим их в памяти модуля,
 * чтобы декодированный битмап не выбрасывался браузером между пересборками DOM —
 * это и есть лечение «вщёлкивания» (повторный декод при перезаходе).
 */
const decoded = new Map<string, HTMLImageElement>();

/** Идемпотентность: однажды запущенный прогон переиспользуется (не дёргаем сеть/декод 2 раза). */
let inFlight: Promise<void> | null = null;

/** Кол-во цветов самоцветов (= радуга 7, canon). Локальная константа — не magic-число. */
const GEM_COLOR_COUNT = 7;

/**
 * Живой контейнер, удерживающий ДЕКОД CSS-масок пустого гнезда (`gemEmptyGlyph`). Маска
 * декодится ОТДЕЛЬНО от `<img>` (см. шапку) — этот удержанный набор не даёт браузеру
 * выбросить декод масок, чтобы силуэты не «вщёлкивались». `null` до первого прогрева.
 */
let gemMaskWarm: HTMLElement | null = null;

/**
 * Прогреть + УДЕРЖАТЬ декод масок пустого гнезда (`gemEmptyGlyph` = `mask:url(gem.png)`).
 * Монтирует скрытый (off-screen, НЕ `display:none` — маска декодится только у ОТРИСОВАННОГО
 * элемента), неинтерактивный набор реальных `gemEmptyGlyph` всех 7 цветов в `document.body`
 * и держит ссылку. Идемпотентно. SSR / без DOM — no-op. Best-effort: визуальной/тестовой
 * стоимости нет (контейнер вне экрана, aria-hidden).
 */
function warmGemMasks(): void {
  if (gemMaskWarm || typeof document === 'undefined' || !document.body) return;
  const host = document.createElement('div');
  host.setAttribute('aria-hidden', 'true');
  host.dataset.gemMaskWarm = 'true';
  // Отрисован (чтобы маска декодилась), но вне экрана и неинтерактивен; НЕ display:none.
  host.style.cssText = 'position:fixed;left:-9999px;top:0;pointer-events:none;';
  for (let color = 1; color <= GEM_COLOR_COUNT; color++) {
    host.insertAdjacentHTML('beforeend', gemEmptyGlyph(color as ColorIndex));
  }
  // Силуэт сам 0-размерный (размер задаёт потребитель через CSS) → даём явный размер,
  // чтобы маска реально отрисовалась/задекодилась.
  for (const child of Array.from(host.children)) {
    (child as HTMLElement).style.width = '32px';
    (child as HTMLElement).style.height = '32px';
  }
  document.body.appendChild(host);
  gemMaskWarm = host;
}

/**
 * Загрузить и декодировать один URL, удержав живую ссылку. `img.decode()` декодирует
 * вне main-thread (ui-performance) и резолвится, когда битмап готов к мгновенной отрисовке.
 * Best-effort: ошибка декода/сети одного ассета НЕ валит остальные (резолвим тихо —
 * отсутствие прогрева ≠ поломка экрана, просто без выигрыша по этому спрайту).
 */
async function warmOne(url: string): Promise<void> {
  if (decoded.has(url)) return;
  // SSR / тестовая среда без DOM — нечего греть (jsdom без Image тоже сюда).
  if (typeof Image === 'undefined') return;
  const img = new Image();
  img.decoding = 'async';
  img.src = url;
  try {
    await img.decode();
  } catch {
    // Декод не удался (битый ассет / среда без декодера) — не держим ссылку, не падаем.
    return;
  }
  // Удерживаем ДЕКОДИРОВАННУЮ ссылку живой (ключ — url, чтобы повторный вызов был no-op).
  decoded.set(url, img);
}

/**
 * Предзагрузить + декодировать ВСЕ растровые мета-ассеты (звери / 7 камней / монета /
 * осколок) и удержать живые ссылки. Идемпотентно: повторный вызов переиспользует уже
 * запущенный/завершённый прогон. Зовётся на старте приложения И при входе в Мастерскую
 * (дёшево — Map уже полна на 2-й раз).
 *
 * @returns промис, который резолвится, когда прогрев завершён (best-effort — отдельные
 *          неудачи не отклоняют общий промис).
 */
export function preloadMetaAssets(): Promise<void> {
  if (inFlight) return inFlight;
  // Маски пустого гнезда — отдельный декод от `<img>`; прогреваем синхронно (удержание
  // отрисованного набора), параллельно с img-декодом ниже.
  warmGemMasks();
  inFlight = Promise.all(META_ASSET_URLS.map((url) => warmOne(url))).then(() => undefined);
  return inFlight;
}

/**
 * Сколько ассетов уже прогрето (живых декодированных ссылок). Для тестов/диагностики —
 * подтверждает, что прогрев реально удержал ссылки.
 */
export function warmedMetaAssetCount(): number {
  return decoded.size;
}

/**
 * Вернуть УЖЕ ДЕКОДИРОВАННЫЙ `HTMLImageElement` для `url` СИНХРОННО, или `null`, если
 * ассет ещё не прогрет (декод не завершён / другая среда). Зачем (feat/fe-hydration п.5 /
 * AC-7/AC-9): потребителю (`lib/gnome-frame-aspect`) нужны natural-размеры спрайтов ДО
 * первого пэйнта карточек, чтобы поставить `--gnc-frame-aspect` без прыжка ширины. На
 * тёплом пути (boot уже прогрел `preloadMetaAssets()`) этот синхронный доступ даёт готовый
 * декодированный `img` → аспект считается синхронно, CLS = 0. Чистый read удержанной Map —
 * server-truth/движка не касается (UX-проекция показа, AC-13).
 */
export function getDecodedMetaAsset(url: string): HTMLImageElement | null {
  return decoded.get(url) ?? null;
}

/**
 * Сколько масок пустого гнезда удержано в прогреве (0 до прогрева, иначе число цветов).
 * Для тестов/диагностики — подтверждает, что маски прогреты отдельно от img.
 */
export function warmedGemMaskCount(): number {
  return gemMaskWarm ? gemMaskWarm.children.length : 0;
}
